export const OFFER_PLAN_MONTHS = [1, 3, 6, 12] as const;

export type OfferPlanMonths = (typeof OFFER_PLAN_MONTHS)[number];
export type PricingMarket = "RU" | "GLOBAL";
export type PricingFailure = "aborted" | "http" | "malformed" | "network" | "timeout";

export interface PricingPort {
  load(locale: string, signal: AbortSignal): Promise<unknown>;
}

export class PricingPortError extends Error {
  constructor(
    readonly kind: Exclude<PricingFailure, "malformed">,
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "PricingPortError";
  }
}

export interface ValidatedPricingPlan {
  readonly plan: string;
  readonly months: OfferPlanMonths;
  readonly currency: string;
  readonly baseAmountMinor: number;
  readonly effectiveAmountMinor: number;
  readonly welcomeApplied: boolean;
}

export interface ValidatedPricing {
  readonly market: PricingMarket;
  readonly currency: string;
  readonly plans: readonly ValidatedPricingPlan[];
}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isPlanMonths = (value: number): value is OfferPlanMonths =>
  OFFER_PLAN_MONTHS.some((months) => months === value);

export function pricingTierForHost(hostname: string, buildMode: string = "production"): PricingMarket {
  const host = hostname.toLowerCase();
  if (host === "blenderbridge.ru" || host === "www.blenderbridge.ru") return "RU";
  if (host === "blenderbridge.com" || host === "www.blenderbridge.com") return "GLOBAL";
  // Local previews follow the regional build; language only controls formatting.
  return buildMode === "ru" ? "RU" : "GLOBAL";
}

export class ApiPricingPort implements PricingPort {
  constructor(
    private readonly fetcher: FetchLike = globalThis.fetch.bind(globalThis),
    private readonly timeoutMs = 8_000,
    private readonly market: PricingMarket = pricingTierForHost(globalThis.location?.hostname ?? "", import.meta.env.MODE),
  ) {}

  async load(_locale: string, signal: AbortSignal): Promise<unknown> {
    const requestController = new AbortController();
    let timedOut = false;
    const forwardAbort = (): void => requestController.abort(signal.reason);
    if (signal.aborted) forwardAbort();
    else signal.addEventListener("abort", forwardAbort, { once: true });
    const timeout = globalThis.setTimeout(() => {
      timedOut = true;
      requestController.abort(new DOMException("Pricing request timed out.", "TimeoutError"));
    }, this.timeoutMs);

    try {
      const response = await this.fetcher(`/api/pricing?tier=${encodeURIComponent(this.market)}`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "omit",
        signal: requestController.signal,
      });
      if (!response.ok) {
        throw new PricingPortError("http", `Pricing request failed with HTTP ${response.status}.`, response.status);
      }
      return await response.json();
    } catch (error) {
      if (error instanceof PricingPortError) throw error;
      if (timedOut) throw new PricingPortError("timeout", "Pricing request timed out.");
      if (signal.aborted) throw new PricingPortError("aborted", "Pricing request was aborted.");
      throw new PricingPortError("network", "Pricing request failed.");
    } finally {
      globalThis.clearTimeout(timeout);
      signal.removeEventListener("abort", forwardAbort);
    }
  }
}

export function validatePricingPayload(payload: unknown): ValidatedPricing {
  if (!isRecord(payload) || payload.ok !== true) throw new Error("malformed_pricing_payload");
  const market = payload.tier;
  const currency = payload.currency;
  const rawPlans = payload.plans;
  if ((market !== "RU" && market !== "GLOBAL") || typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    throw new Error("malformed_pricing_market");
  }
  if (!Array.isArray(rawPlans)) throw new Error("malformed_pricing_plans");

  const plans = new Map<OfferPlanMonths, ValidatedPricingPlan>();
  for (const rawPlan of rawPlans) {
    if (!isRecord(rawPlan)) throw new Error("malformed_pricing_plan");
    const months = rawPlan.duration_months;
    const baseAmountMinor = rawPlan.base_amount_minor;
    const effectiveAmountMinor = rawPlan.effective_amount_minor;
    if (
      typeof months !== "number" || !Number.isInteger(months) || !isPlanMonths(months)
      || typeof rawPlan.plan !== "string" || rawPlan.plan.length === 0
      || rawPlan.currency !== currency
      || typeof baseAmountMinor !== "number" || !Number.isSafeInteger(baseAmountMinor) || baseAmountMinor < 0
      || typeof effectiveAmountMinor !== "number" || !Number.isSafeInteger(effectiveAmountMinor) || effectiveAmountMinor < 0
      || effectiveAmountMinor > baseAmountMinor
      || typeof rawPlan.welcome_applied !== "boolean"
      || plans.has(months)
    ) {
      throw new Error("malformed_pricing_plan");
    }
    plans.set(months, {
      plan: rawPlan.plan,
      months,
      currency,
      baseAmountMinor,
      effectiveAmountMinor,
      welcomeApplied: rawPlan.welcome_applied,
    });
  }

  const ordered = OFFER_PLAN_MONTHS.map((months) => plans.get(months));
  if (ordered.some((plan) => !plan)) throw new Error("malformed_pricing_durations");
  return { market, currency, plans: ordered as readonly ValidatedPricingPlan[] };
}

export function recommendedPlanMonths(plans: readonly ValidatedPricingPlan[]): OfferPlanMonths | null {
  const winners = plans.filter((candidate) => plans.every((other) => (
    candidate.months === other.months
    || candidate.effectiveAmountMinor * other.months < other.effectiveAmountMinor * candidate.months
  )));
  return winners.length === 1 ? winners[0]?.months ?? null : null;
}
